/**
 * Time on Page Tracking for AIALGO.trading
 * Accurately measures time spent on pages for engagement analytics
 */

interface TimeOnPageConfig {
  heartbeatInterval?: number // Send heartbeat events every X ms
  inactiveThreshold?: number // User considered inactive after X ms of no activity
  maxDuration?: number // Max duration to track (ms) to prevent inflated stats
}

interface TimeOnPageData {
  page: string
  startTime: number
  endTime?: number
  duration: number
  activeTime: number
  engagementEvents: number
  isActive: boolean
}

class TimeOnPageTracker {
  private heartbeatInterval: number
  private inactiveThreshold: number
  private maxDuration: number
  private currentPage: string
  private sessionStartTime: number
  private lastActivityTime: number
  private heartbeatTimer: number | null = null
  private activityEventListeners: Array<() => void> = []
  private engagementEvents: number = 0
  private isActive: boolean = true

  constructor(config: TimeOnPageConfig = {}) {
    this.heartbeatInterval = config.heartbeatInterval || 30000 // 30 seconds
    this.inactiveThreshold = config.inactiveThreshold || 60000 // 1 minute
    this.maxDuration = config.maxDuration || 1800000 // 30 minutes max
    this.currentPage = window.location.pathname
    this.sessionStartTime = Date.now()
    this.lastActivityTime = Date.now()

    if (typeof window !== 'undefined') {
      this.initializeTracking()
    }
  }

  private initializeTracking(): void {
    // Track user activity
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart']

    activityEvents.forEach(eventType => {
      const handler = () => {
        this.onActivity()
      }
      document.addEventListener(eventType, handler, { passive: true })
      this.activityEventListeners.push(() => {
        document.removeEventListener(eventType, handler)
      })
    })

    // Start heartbeat
    this.startHeartbeat()

    // Track visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseTracking()
      } else {
        this.resumeTracking()
      }
    })

    // Send final time on page when leaving
    window.addEventListener('beforeunload', () => {
      this.sendFinalTimeOnPage()
    })

    // Track page unload
    window.addEventListener('pagehide', () => {
      this.sendFinalTimeOnPage()
    })
  }

  private onActivity(): void {
    this.lastActivityTime = Date.now()
    this.isActive = true
    this.engagementEvents++
  }

  private startHeartbeat(): void {
    this.heartbeatTimer = window.setInterval(() => {
      this.checkInactivity()
      this.sendHeartbeat()
    }, this.heartbeatInterval)
  }

  private checkInactivity(): void {
    const timeSinceLastActivity = Date.now() - this.lastActivityTime

    if (timeSinceLastActivity > this.inactiveThreshold) {
      this.isActive = false
    }
  }

  private sendHeartbeat(): void {
    const currentTime = Date.now()
    const timeOnPage = currentTime - this.sessionStartTime
    const activeTime = this.isActive ? timeOnPage : this.lastActivityTime - this.sessionStartTime

    // Cap at max duration
    const cappedDuration = Math.min(timeOnPage, this.maxDuration)

    // Send heartbeat to PostHog
    if (typeof window !== 'undefined' && (window as any).posthog?.__loaded) {
      const posthog = (window as any).posthog
      posthog.capture('time_on_page_heartbeat', {
        page: this.currentPage,
        time_on_page_ms: cappedDuration,
        active_time_ms: activeTime,
        engagement_events: this.engagementEvents,
        is_active: this.isActive,
        session_id: this.getSessionId(),
      })
    }
  }

  private sendFinalTimeOnPage(): void {
    const currentTime = Date.now()
    const timeOnPage = currentTime - this.sessionStartTime
    const activeTime = this.isActive ? timeOnPage : this.lastActivityTime - this.sessionStartTime

    // Cap at max duration
    const cappedDuration = Math.min(timeOnPage, this.maxDuration)

    const data: TimeOnPageData = {
      page: this.currentPage,
      startTime: this.sessionStartTime,
      endTime: currentTime,
      duration: cappedDuration,
      activeTime,
      engagementEvents: this.engagementEvents,
      isActive: this.isActive,
    }

    // Send to PostHog
    if (typeof window !== 'undefined' && (window as any).posthog?.__loaded) {
      const posthog = (window as any).posthog
      posthog.capture('time_on_page_final', {
        page: this.currentPage,
        time_on_page_ms: cappedDuration,
        active_time_ms: activeTime,
        engagement_events: this.engagementEvents,
        is_active: this.isActive,
        session_id: this.getSessionId(),
      })

      // Also set as user property for average calculation
      posthog.setPeopleProperties({
        avg_time_on_page: cappedDuration,
        last_page_visit: this.currentPage,
      })
    }

    console.log('[Analytics] Time on page:', {
      page: this.currentPage,
      duration: Math.round(cappedDuration / 1000),
      active: Math.round(activeTime / 1000),
    })

    // Cleanup
    this.cleanup()
  }

  private pauseTracking(): void {
    // Pause tracking when tab is hidden
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }

    // Send paused event
    if (typeof window !== 'undefined' && (window as any).posthog?.__loaded) {
      const posthog = (window as any).posthog
      posthog.capture('time_on_page_paused', {
        page: this.currentPage,
        time_elapsed_ms: Date.now() - this.sessionStartTime,
      })
    }
  }

  private resumeTracking(): void {
    // Resume tracking when tab becomes visible again
    this.lastActivityTime = Date.now()
    this.isActive = true
    this.startHeartbeat()

    // Send resumed event
    if (typeof window !== 'undefined' && (window as any).posthog?.__loaded) {
      const posthog = (window as any).posthog
      posthog.capture('time_on_page_resumed', {
        page: this.currentPage,
      })
    }
  }

  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('time_session_id')
    if (!sessionId) {
      sessionId = `time_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      sessionStorage.setItem('time_session_id', sessionId)
    }
    return sessionId
  }

  private cleanup(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }

    // Remove event listeners
    this.activityEventListeners.forEach(cleanup => cleanup())
    this.activityEventListeners = []
  }

  /**
   * Get current time on page statistics
   */
  getStats(): {
    duration: number
    activeTime: number
    engagementEvents: number
    isActive: boolean
  } {
    const currentTime = Date.now()
    const timeOnPage = currentTime - this.sessionStartTime
    const activeTime = this.isActive ? timeOnPage : this.lastActivityTime - this.sessionStartTime

    return {
      duration: timeOnPage,
      activeTime,
      engagementEvents: this.engagementEvents,
      isActive: this.isActive,
    }
  }
}

// Singleton instance
let timeTracker: TimeOnPageTracker | null = null

/**
 * Initialize time on page tracking
 */
export function initTimeOnPageTracking(config?: TimeOnPageConfig): TimeOnPageTracker {
  // End previous session if exists
  if (timeTracker) {
    timeTracker['sendFinalTimeOnPage']()
  }

  timeTracker = new TimeOnPageTracker(config)
  return timeTracker
}

/**
 * Get time tracker instance
 */
export function getTimeOnPageTracker(): TimeOnPageTracker | null {
  return timeTracker
}

/**
 * Reset tracking (call on route changes)
 */
export function resetTimeOnPageTracking(): void {
  if (timeTracker) {
    timeTracker['sendFinalTimeOnPage']()
    timeTracker = null
  }
  initTimeOnPageTracking()
}
